import { useCallback, useEffect, useRef } from 'react';

import { usePrint } from './usePrint';

export type BarcodeScannerOptions = {
  maxInterval?: number;
  minLength?: number;
  terminator?: string;
  preventDefault?: boolean;
  enabled?: boolean;
  withPrint?: boolean;
};

/*
 Some test examples how to test barcode readers
 change payload to what need
(() => {
  const payload = `{
    "manufactureID": "2",
    "lockerbankName": "abcd",
    "manufacturerLockerID": "41"
  }`;

  const fire = (key) =>
    document.body.dispatchEvent(
      new KeyboardEvent('keydown', { key, bubbles: true })
    );

  for (const ch of payload) fire(ch);
  fire('Enter');
})();

*/

export type OnScan = (code: string) => void;

export type SelectedPieceId = string | number | null;

type ScannerState = {
  buffer: string;
  lastTimestamp: number;
  printTimeout: ReturnType<typeof setTimeout> | null;
};

type LatestProps = {
  onScan: OnScan;
  selectedPieceId?: SelectedPieceId | undefined;
};

const initialState = {
    buffer: '',
    lastTimestamp: 0,
    printTimeout: null,
  };

export const useBarcodeScanner = (
  onScan: OnScan,
  selectedPieceId?: SelectedPieceId | undefined,
  options: BarcodeScannerOptions = {},
) => {
  const {
    maxInterval = 50,
    minLength = 6,
    terminator = 'Enter',
    preventDefault = true,
    enabled = true,
    withPrint = true,
  } = options;

  const propsRef = useRef<LatestProps>({ onScan, selectedPieceId });

  const { print } = usePrint();
  useEffect(() => {
    propsRef.current = { onScan, selectedPieceId };
  }, [onScan, selectedPieceId]);

  const stateRef = useRef<ScannerState>({ ...initialState });

  const stopPrint = useCallback(() => {
    if (stateRef.current.printTimeout) {
      clearTimeout(stateRef.current.printTimeout);
      stateRef.current.printTimeout = null;
    }
  }, []);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      const { key } = e;
      if ((key.length > 1 && key !== terminator) || e.repeat) {
        return;
      }

      const state = stateRef.current;
      const interval = e.timeStamp - state.lastTimestamp;

      const isExceededInterval = interval > maxInterval;

      if (state.lastTimestamp && stateRef.current.printTimeout) {
        stopPrint();
      }

      if (
        key.toLowerCase() === 'p' &&
        isExceededInterval &&
        withPrint &&
        // https://github.com/microsoft/TypeScript/issues/5901#issuecomment-396497729
        // unbelievable
        (e.target as HTMLElement)?.tagName.toLowerCase() !== 'input' &&
        propsRef.current.selectedPieceId
      ) {
        if (propsRef.current.selectedPieceId) {
          stateRef.current.printTimeout = setTimeout(
            () => {
              stateRef.current = { ...initialState };
              print(+(propsRef.current.selectedPieceId || 0));
            },
            maxInterval + 20,
          );
        }
      }

      if (key === terminator) {
        if (state.buffer.length >= minLength) {
          if (preventDefault) e.preventDefault();
          propsRef.current.onScan(state.buffer);
        }
        state.buffer = '';
        state.lastTimestamp = 0;
        return;
      } else {
        if (isExceededInterval) {
          state.buffer = key;
        } else {
          state.buffer += key;
        }

        if (state.buffer.length >= minLength) {
          // https://github.com/microsoft/TypeScript/issues/5901#issuecomment-396497729
          // unbelievable
          (document.activeElement as HTMLElement)?.blur?.();
        }

        state.lastTimestamp = e.timeStamp;
      }
    },
    [maxInterval, minLength, terminator, preventDefault, withPrint, print, stopPrint],
  );

  useEffect(() => {
    if (!enabled) return;
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      stateRef.current = { ...initialState };
      window.removeEventListener('keydown', handleKeyDown);
      stopPrint();
    };
  }, [enabled, handleKeyDown, stopPrint]);
};
